import React, { useState, useRef, useCallback } from 'react';
import { useAppStore, ALL_PROPS, KeyframeProp, LayerKeyframes } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { SelectedLayerKf, SNAP_PX } from '../components/timeline/timelineConstants';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DragState {
  layerId: string;
  prop: KeyframeProp | 'summary';
  fromFrame: number;
  toFrame: number;
  /** true = the dragged kf was part of the selection → all selected kfs move together */
  isMulti: boolean;
}

interface Options {
  trackScrollRef: React.RefObject<HTMLDivElement | null>;
  pxPerFrame: number;
  totalFrames: number;
  selectedKfsRef: React.MutableRefObject<SelectedLayerKf[]>;
  setSelectedKfs: (kfs: SelectedLayerKf[]) => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useLayerKfDrag({ trackScrollRef, pxPerFrame, totalFrames, selectedKfsRef, setSelectedKfs }: Options) {
  const setCurrentFrame = useAppStore((s) => s.setCurrentFrame);

  const [dragState, setDragState] = useState<DragState | null>(null);
  const kfDragRef      = useRef<DragState | null>(null);
  const kfDragMovedRef = useRef(false);

  const setSelectedKfsRef = useRef(setSelectedKfs);
  setSelectedKfsRef.current = setSelectedKfs;

  const handleKfPointerDown = useCallback(
    (e: React.PointerEvent, layerId: string, prop: KeyframeProp | 'summary', frame: number) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      // Determine if this kf is part of the current selection so we know
      // whether to drag all selected kfs together.
      const sel = selectedKfsRef.current;
      const inSelection = prop === 'summary'
        ? sel.some((k) => k.layerId === layerId && k.frame === frame)
        : sel.some((k) => k.layerId === layerId && k.prop === prop && k.frame === frame);
      const drag = { layerId, prop, fromFrame: frame, toFrame: frame, isMulti: inSelection && sel.length > 0 };
      kfDragRef.current = drag;
      kfDragMovedRef.current = false;
      setDragState(drag);
    },
    [selectedKfsRef]
  );

  const handleKfPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!kfDragRef.current) return;
      const cursorPx = e.clientX - (trackScrollRef.current?.getBoundingClientRect().left ?? 0) + (trackScrollRef.current?.scrollLeft ?? 0);
      let newFrame = Math.max(0, Math.min(totalFrames - 1, Math.round(cursorPx / pxPerFrame)));
      if (e.shiftKey) {
        const cf = Math.round(useAppStore.getState().currentFrame);
        for (const f of [0, totalFrames - 1, cf]) {
          if (Math.abs(cursorPx - f * pxPerFrame) <= SNAP_PX) { newFrame = f; break; }
        }
      }
      if (newFrame !== kfDragRef.current.toFrame) {
        const next = { ...kfDragRef.current, toFrame: newFrame };
        kfDragRef.current = next;
        kfDragMovedRef.current = true;
        setDragState({ ...next });
      }
    },
    [pxPerFrame, totalFrames, trackScrollRef]
  );

  const handleKfPointerUp = useCallback(
    (e: React.PointerEvent, layerId: string, prop: KeyframeProp | 'summary', fromFrame: number) => {
      const drag = kfDragRef.current;
      if (!drag || drag.layerId !== layerId || drag.prop !== prop || drag.fromFrame !== fromFrame) return;
      const { toFrame, isMulti } = drag;
      if (kfDragMovedRef.current && fromFrame !== toFrame) {
        viewportActions.pushSnapshot.current();
        const delta = toFrame - fromFrame;
        const state  = useAppStore.getState();
        if (isMulti) {
          // Collect all moves first so remove+set doesn't stomp coincident frames.
          const selected = selectedKfsRef.current;
          const moves: { layerId: string; prop: KeyframeProp; from: number; to: number; value: boolean | number }[] = [];
          for (const kf of selected) {
            const layerKfs = state.layerKeyframes[kf.layerId];
            if (!layerKfs || !(kf.frame in layerKfs[kf.prop])) continue;
            const newFrame = Math.max(0, Math.min(totalFrames - 1, kf.frame + delta));
            moves.push({
              layerId: kf.layerId, prop: kf.prop,
              from: kf.frame, to: newFrame,
              value: (layerKfs[kf.prop] as Record<number, boolean | number>)[kf.frame],
            });
          }
          // Single store update — all layers patched in one pass
          useAppStore.setState((s) => {
            const lockedIds = new Set(s.layers.filter((l) => l.locked).map((l) => l.id));
            const nextKfs: Record<string, LayerKeyframes> = {};
            for (const m of moves) {
              if (lockedIds.has(m.layerId)) continue;
              if (!nextKfs[m.layerId]) {
                const src = s.layerKeyframes[m.layerId];
                if (!src) continue;
                nextKfs[m.layerId] = {
                  opacity: { ...src.opacity },
                  rotateX: { ...src.rotateX }, rotateY: { ...src.rotateY }, rotateZ: { ...src.rotateZ },
                  translateX: { ...src.translateX }, translateY: { ...src.translateY },
                };
              }
              const propKfs = nextKfs[m.layerId][m.prop] as Record<number, boolean | number>;
              delete propKfs[m.from];
              propKfs[m.to] = m.value;
            }
            return { layerKeyframes: { ...s.layerKeyframes, ...nextKfs } };
          });
          // Update selection to the new frame positions
          setSelectedKfsRef.current(moves.map((m) => ({ layerId: m.layerId, prop: m.prop, frame: m.to })));
        } else {
          // Single-kf or summary-row move — one store update
          useAppStore.setState((s) => {
            const layer = s.layers.find((l) => l.id === layerId);
            if (layer?.locked) return {};
            const src = s.layerKeyframes[layerId];
            if (!src) return {};
            const propsToMove: KeyframeProp[] = prop === 'summary'
              ? ALL_PROPS.filter((p) => fromFrame in src[p])
              : (fromFrame in src[prop] ? [prop as KeyframeProp] : []);
            if (!propsToMove.length) return {};
            const next: LayerKeyframes = {
              opacity: { ...src.opacity },
              rotateX: { ...src.rotateX }, rotateY: { ...src.rotateY }, rotateZ: { ...src.rotateZ },
              translateX: { ...src.translateX }, translateY: { ...src.translateY },
            };
            for (const p of propsToMove) {
              const val = (src[p] as Record<number, boolean | number>)[fromFrame];
              delete (next[p] as Record<number, boolean | number>)[fromFrame];
              (next[p] as Record<number, boolean | number>)[toFrame] = val;
            }
            return { layerKeyframes: { ...s.layerKeyframes, [layerId]: next } };
          });
        }
        setCurrentFrame(toFrame);
      }
      kfDragRef.current = null;
      setDragState(null);
    },
    [setCurrentFrame, selectedKfsRef, totalFrames]
  );

  return { dragState, kfDragMovedRef, handleKfPointerDown, handleKfPointerMove, handleKfPointerUp };
}
